import { memo, useMemo } from 'react';

import {
  BtnGroup,
  BtnToolbar,
  Button,
  ButtonAction,
  Combobox,
  Datepicker,
  type HookFormMethods,
  HookFormResolver,
  Row
} from '@dec/shared';
import { useTranslation } from 'react-i18next';

import {
  PAYMENT_STATES,
  type PaymentFilterForm,
  type PaymentOption,
  paymentSearchValidation
} from './search';

interface PaymentFiltersProps {
  onSearch: (values: PaymentFilterForm) => void;

  onClear: () => void;
}

export const PaymentFilters = memo(({ onSearch, onClear }: PaymentFiltersProps) => {
  const { t } = useTranslation();

  const stateOptions = useMemo<Array<PaymentOption>>(
    () =>
      PAYMENT_STATES.map((state) => ({
        id: state,
        name: t(`common.taxreturn-status.${state}`)
      })),
    [t]
  );

  const handleClear = (reset: HookFormMethods['reset']) => {
    reset({});

    onClear();
  };

  return (
    <HookFormResolver onSubmit={onSearch} validations={paymentSearchValidation}>
      {({ reset }: HookFormMethods) => (
        <>
          <Row>
            <div className="col-12 col-sm-6 col-md-3">
              <Datepicker label={t('common.label.dateFrom')} name="dateFrom" />
            </div>

            <div className="col-12 col-sm-6 col-md-3">
              <Datepicker label={t('common.label.dateTo')} name="dateTo" />
            </div>
            <div className="col-12 col-md-6">
              <Combobox
                code="id"
                display="name"
                data={stateOptions}
                label={t('taxReturns.taxReturnStatus')}
                name="state"
              />
            </div>
          </Row>

          <BtnToolbar>
            <BtnGroup>
              <Button
                action={ButtonAction.clean}
                variant="secondary"
                type="reset"
                onClick={() => handleClear(reset)}
              />
            </BtnGroup>

            <BtnGroup>
              <Button action={ButtonAction.find} type="submit" variant="primary" />
            </BtnGroup>
          </BtnToolbar>
        </>
      )}
    </HookFormResolver>
  );
});

PaymentFilters.displayName = 'PaymentFilters';
